import { supabase } from './supabase';
import {
  resolveWebPageKey,
  shouldThrottlePageVisit,
} from './userActivityLogLogic';

export {
  PAGE_VISIT_THROTTLE_MS,
  resetPageVisitThrottleForTests,
  resolveTrainerPageKey,
  resolveWebPageKey,
  shouldThrottlePageVisit,
} from './userActivityLogLogic';
export type {
  TrainerPageVisitResolution,
  WebPageVisitResolution,
} from './userActivityLogLogic';

export type LogWebPageVisitArgs = {
  /** Null for anonymous public visits. */
  userId: string | null;
  pathname: string;
  /** Required for trainer_dashboard surface. */
  role?: 'trainer' | 'member' | null;
  sessionId?: string | null;
  nowMs?: number;
};

/**
 * Fire-and-forget web page visit (public + trainer). Never throws to callers.
 */
export async function logWebPageVisit(
  args: LogWebPageVisitArgs
): Promise<{ logged: boolean; reason?: string }> {
  try {
    const resolved = resolveWebPageKey(args.pathname);
    if (!resolved) {
      return { logged: false, reason: 'unmapped_path' };
    }

    if (
      resolved.surface === 'trainer_dashboard' &&
      args.role !== 'trainer'
    ) {
      return { logged: false, reason: 'not_trainer' };
    }

    if (resolved.surface === 'public_web') {
      // ok for anon (userId null) or signed-in
    } else if (!args.userId) {
      return { logged: false, reason: 'auth_required' };
    }

    const path = args.pathname.replace(/\/+$/, '') || '/';
    const throttleUser = args.userId ?? 'anon';
    if (
      shouldThrottlePageVisit(
        throttleUser,
        resolved.pageKey,
        path,
        args.nowMs ?? Date.now()
      )
    ) {
      return { logged: false, reason: 'throttled' };
    }

    const clientCreatedAt = new Date(args.nowMs ?? Date.now()).toISOString();
    const { error } = await supabase.from('user_activity_log').insert({
      event_type: 'page_visit',
      platform: 'web',
      surface: resolved.surface,
      page_key: resolved.pageKey,
      user_id: args.userId,
      session_id: args.sessionId ?? null,
      client_created_at: clientCreatedAt,
      payload: {
        path,
        ...(resolved.pathParams ? { path_params: resolved.pathParams } : {}),
      },
    });

    if (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[userActivityLog] insert failed', error.message);
      }
      return { logged: false, reason: 'insert_error' };
    }

    return { logged: true };
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[userActivityLog] unexpected error', err);
    }
    return { logged: false, reason: 'exception' };
  }
}

/** @deprecated Use logWebPageVisit */
export async function logTrainerPageVisit(args: {
  userId: string;
  pathname: string;
  sessionId?: string | null;
  nowMs?: number;
}): Promise<{ logged: boolean; reason?: string }> {
  return logWebPageVisit({
    userId: args.userId,
    pathname: args.pathname,
    role: 'trainer',
    sessionId: args.sessionId,
    nowMs: args.nowMs,
  });
}
